import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { varianceReconciliation } from "@/lib/api";
import { EUR, fmtDate, num, paymentMethodIT, paymentMethodOptions } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Reconciliation() {
  const thisYear = new Date().getFullYear();
  const [dateFrom, setDateFrom] = useState(`${thisYear}-01-01`);
  const [dateTo, setDateTo] = useState(`${thisYear}-12-31`);
  const [method, setMethod] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (method) params.method = method;
      const r = await varianceReconciliation(params);
      setRows(r || []);
    })();
  }, [dateFrom, dateTo, method]);

  const totals = useMemo(() => {
    const total = rows.reduce((s, r) => s + num(r.paid_amount), 0);
    const future = rows.filter((r) => r.delta_days > 0).length;
    const past = rows.filter((r) => r.delta_days < 0).length;
    return { total, future, past };
  }, [rows]);

  return (
    <div data-testid="reconciliation-page">
      <PageHeader
        title="Riconciliazione Cassa ↔ Competenza"
        subtitle="Pagamenti la cui data di esecuzione cade in un mese diverso rispetto alla scadenza pianificata"
        actions={
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500">Dal</Label>
            <Input
              type="date"
              data-testid="input-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[150px]"
            />
            <Label className="text-xs uppercase tracking-wider text-stone-500">Al</Label>
            <Input
              type="date"
              data-testid="input-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[150px]"
            />
            <Label className="text-xs uppercase tracking-wider text-stone-500">Metodo</Label>
            <Select
              value={method || "all"}
              onValueChange={(v) => setMethod(v === "all" ? "" : v)}
            >
              <SelectTrigger
                data-testid="reconciliation-method"
                className="h-9 w-[130px]"
              >
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {paymentMethodOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ExportButtons
              rows={rows.map((r) => ({
                expense_title: r.expense_title,
                vendor_name: r.vendor_name || "",
                postponed: r.postponed ? "Sì" : "No",
                due_date: r.due_date,
                original_due_date: r.original_due_date,
                paid_at: r.paid_at,
                delta_days: r.delta_days,
                accrual_bucket: r.accrual_bucket,
                cash_bucket: r.cash_bucket,
                paid_amount: r.paid_amount,
                method: paymentMethodIT[r.method] || paymentMethodIT.unspecified,
                reference: r.reference || "",
              }))}
              columns={[
                { key: "expense_title", header: "Scadenza" },
                { key: "vendor_name", header: "Fornitore" },
                { key: "postponed", header: "Rinviata" },
                { key: "due_date", header: "Scadenza", type: "date" },
                {
                  key: "original_due_date",
                  header: "Scadenza originale",
                  type: "date",
                },
                { key: "paid_at", header: "Pagato il", type: "date" },
                { key: "delta_days", header: "Δ giorni", type: "integer" },
                { key: "accrual_bucket", header: "Bucket competenza" },
                { key: "cash_bucket", header: "Bucket cassa" },
                { key: "paid_amount", header: "Importo (€)", type: "currency" },
                { key: "method", header: "Metodo" },
                { key: "reference", header: "Riferimento" },
              ]}
              filenameBase={buildFilename("riconciliazione_cassa_competenza", {
                date_from: dateFrom,
                date_to: dateTo,
              })}
              sheetName="Riconciliazione"
              testId="export-reconciliation"
            />
          </div>
        }
      />

      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900">
        <strong>Cosa leggere qui.</strong> Ogni riga è un pagamento che appare in un mese sotto la
        vista <em>Cassa</em> e in un altro mese sotto la vista <em>Competenza</em>. La somma delle
        righe corrisponde esattamente alla differenza fra le due viste nello scostamento mensile.
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard testId="stat-reconciliation-count" label="Voci divergenti" value={rows.length} isCurrency={false} />
        <StatCard testId="stat-reconciliation-amount" label="Importo complessivo" value={totals.total} />
        <StatCard
          testId="stat-reconciliation-split"
          label="Pagate in anticipo / ritardo"
          value={`${totals.past} / ${totals.future}`}
          isCurrency={false}
          tone="info"
        />
      </div>

      <div className="mt-8 rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scadenza / Fornitore</TableHead>
              <TableHead>Scadenza (competenza)</TableHead>
              <TableHead>Pagato il (cassa)</TableHead>
              <TableHead>Δ giorni</TableHead>
              <TableHead>Competenza</TableHead>
              <TableHead>Cassa</TableHead>
              <TableHead className="text-right">Importo</TableHead>
              <TableHead>Metodo</TableHead>
              <TableHead>Riferimento</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-12 text-center">
                  <p className="text-sm text-stone-500">
                    Nessuna divergenza nel periodo: cassa e competenza coincidono.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.payment_id} data-testid={`reconciliation-row-${r.payment_id}`}>
                <TableCell>
                  <div className="font-medium text-stone-900">{r.expense_title}</div>
                  <div className="text-xs text-stone-500">{r.vendor_name || "—"}</div>
                  {r.postponed && (
                    <div className="mt-0.5 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] uppercase text-blue-700">
                      Rinviata
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-mono text-sm">{fmtDate(r.due_date)}</div>
                  {r.postponed && (
                    <div className="text-xs text-stone-500">
                      Orig.: {fmtDate(r.original_due_date)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">{fmtDate(r.paid_at)}</TableCell>
                <TableCell className="font-mono text-sm tabular-nums">
                  <span className={r.delta_days >= 0 ? "text-amber-700" : "text-blue-700"}>
                    {r.delta_days >= 0 ? "+" : ""}
                    {r.delta_days} gg
                  </span>
                </TableCell>
                <TableCell className="font-mono text-sm">{r.accrual_bucket}</TableCell>
                <TableCell className="font-mono text-sm">{r.cash_bucket}</TableCell>
                <TableCell className="text-right font-mono tabular-nums font-medium">
                  {EUR(r.paid_amount)}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-700">
                    {paymentMethodIT[r.method] || paymentMethodIT.unspecified}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-stone-500">{r.reference || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
